import { useEffect, useRef, useState } from 'react';

interface PlayerAreaProps {
  title: string;
  poster: string;
  episodeLabel?: string;
}

type PlayerState = 'idle' | 'loading' | 'playing' | 'error';

export default function PlayerArea({ title, poster, episodeLabel }: PlayerAreaProps) {
  const [state, setState] = useState<PlayerState>('idle');
  const timerRef = useRef<number | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    setState('idle');
    clearTimer();
    return clearTimer;
  }, [episodeLabel]);

  const handlePlay = () => {
    setState('loading');
    clearTimer();
    timerRef.current = window.setTimeout(() => setState('playing'), 1200);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
      <div className="relative aspect-video w-full overflow-hidden bg-background-950">
        <img
          src={poster}
          alt={`${title} 播放预览`}
          className={`h-full w-full object-cover object-top transition-all duration-500 ${
            state === 'playing' ? 'scale-105 opacity-40 blur-sm' : 'opacity-70'
          }`}
        />

        {state === 'idle' ? (
          <button
            type="button"
            onClick={handlePlay}
            aria-label={`播放 ${title}`}
            className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:scale-110">
              <i className="ri-play-fill text-3xl"></i>
            </span>
            <span className="text-sm text-foreground-700">点击播放（演示）</span>
          </button>
        ) : null}

        {state === 'loading' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <span className="h-10 w-10 animate-spin rounded-full border-2 border-background-300 border-t-primary-500"></span>
            <span className="text-sm text-foreground-600">正在加载播放资源…</span>
          </div>
        ) : null}

        {state === 'playing' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/20 text-primary-500">
              <i className="ri-movie-2-line text-2xl"></i>
            </span>
            <span className="font-heading text-base font-semibold text-foreground-950">
              演示播放中 · {episodeLabel ? episodeLabel : title}
            </span>
            <span className="text-xs text-foreground-600">本站播放区域为界面演示，未接入真实视频源</span>
          </div>
        ) : null}

        {state === 'error' ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500/20 text-accent-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </span>
            <span className="font-heading text-base font-semibold text-foreground-950">视频加载失败</span>
            <span className="text-xs text-foreground-600">资源暂时无法加载，请稍后重试</span>
            <button
              type="button"
              onClick={handlePlay}
              className="mt-1 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重试播放
            </button>
          </div>
        ) : null}

        {state !== 'error' && state !== 'idle' ? (
          <span className="absolute left-3 top-3 rounded-md bg-background-950/70 px-2 py-1 text-xs text-foreground-800 backdrop-blur">
            演示模式
          </span>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-background-950 to-transparent px-4 pb-3 pt-8 text-foreground-700">
          <i className={`${state === 'playing' ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`}></i>
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-background-300/70">
            <div className={`h-full rounded-full bg-primary-500 transition-all duration-[8000ms] ${state === 'playing' ? 'w-1/2' : 'w-0'}`}></div>
          </div>
          <span className="text-xs">1080P</span>
          <i className="ri-volume-up-line text-base"></i>
          <i className="ri-fullscreen-line text-base"></i>
          <button
            type="button"
            onClick={() => setState('error')}
            className="cursor-pointer text-xs text-foreground-600 transition-colors hover:text-accent-400"
          >
            遇到问题？
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <p className="text-xs text-foreground-500">
          <i className="ri-information-line mr-1"></i>
          播放功能为前端演示，未接入真实视频，不支持在线播放与下载
        </p>
        <span className="text-xs text-foreground-500">字幕：简体中文 / 英文</span>
      </div>
    </div>
  );
}